import { useMemo } from 'react';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import { DefinitionTooltipID } from 'src/components/Tooltip/DefinitionTooltip/definitionTooltipConfig';
import SortCaret, { SortDirection } from 'src/components/Tables/SortCaret';
import { cj } from 'src/utils/classNames';
import { WithClassName } from 'src/utils/UtilityTypes';
import { HeaderKey } from './types';

interface TableHeaderProps extends WithClassName {
	handleUpdateSort: (key: HeaderKey) => void;
	currentSortHeader: HeaderKey;
	sortState: SortDirection;
	isPortfolioPage?: boolean;
}

interface Header {
	label: string;
	sortKey: HeaderKey | null;
	tooltipDefinitionId?: DefinitionTooltipID;
}

const TableHeader = ({
	className,
	handleUpdateSort,
	currentSortHeader,
	sortState,
	isPortfolioPage,
}: TableHeaderProps) => {
	const headers: Header[] = useMemo(() => {
		return [
			{ label: 'Vault', sortKey: null },
			...((!isPortfolioPage
				? [
						{
							label: 'Vault Balance',
							sortKey: 'vaultBalance',
						},
						{ label: 'APY', sortKey: 'apy' },
					]
				: []) satisfies Header[]),
			{
				label: 'Your Balance',
				sortKey: 'currentStake',
				tooltipDefinitionId: isPortfolioPage
					? undefined
					: 'insuranceFundVaultsYourBalance',
			},
			{
				label: 'Your Earnings',
				sortKey: 'cumulativeEarnings',
				tooltipDefinitionId: isPortfolioPage
					? undefined
					: 'insuranceFundVaultsYourEarnings',
			},
			{ label: isPortfolioPage ? 'Action' : 'Actions', sortKey: null },
		];
	}, [isPortfolioPage]);

	const style = useMemo(() => {
		const numCols = isPortfolioPage ? 4 : 6;

		return { gridColumn: `span ${numCols} / span ${numCols}` };
	}, [isPortfolioPage]);

	return (
		<div
			className={cj(
				'grid grid-cols-subgrid border-b border-stroke-divider px-md py-sm',
				className
			)}
			style={style}
		>
			{headers.map((header) => (
				<div
					key={header.label.replace(/ /g, '') + header.sortKey}
					className={cj(
						'flex items-center gap-x-1 typo-b4 text-text-secondary',
						header.label.startsWith('Action') && 'justify-end',
						header.sortKey && 'cursor-pointer'
					)}
					onClick={() => {
						if (header.sortKey) {
							handleUpdateSort(header.sortKey);
						}
					}}
				>
					<div>
						<DefinitionTooltip definitionId={header.tooltipDefinitionId}>
							{header.label}
						</DefinitionTooltip>
					</div>
					{header.sortKey && (
						<SortCaret
							state={
								currentSortHeader === header.sortKey ? sortState : 'neutral'
							}
						/>
					)}
				</div>
			))}
		</div>
	);
};

export default TableHeader;
